import { SpeechifyPersistedStoreState } from 'store';
// eslint-disable-next-line no-restricted-imports
import type { StoreApi } from 'zustand';
import type { PersistOptions as ZustandPersistOptions } from 'zustand/middleware';

export interface PersistentStoreOptions<T, PersistedState = T> {
  storageName: string;
  version?: ZustandPersistOptions<T>['version'];
  migrate?: ZustandPersistOptions<T>['migrate'];
  partialize?: ZustandPersistOptions<T, PersistedState>['partialize'];
  backfillStateFromReduxPersist?: (reduxPersistedState: SpeechifyPersistedStoreState, defaultState: T) => T;
  hydrateAtLoadTime?: boolean;
}

export type StateCreator<T> = () => T;

export enum StoreType {
  indexedDB = 'indexedDB',
  localStorage = 'localStorage',
  nonPersistent = 'nonPersistent'
}

type BaseStoreInstance<State> = {
  (): State;
  <PartialState>(selector: (state: State) => PartialState): PartialState;
  getState: StoreApi<State>['getState'];
  setState: StoreApi<State>['setState'];
  subscribe: StoreApi<State>['subscribe'];
  registerOnResetCleanup: (fn: () => void) => void;
  reset: () => void | Promise<void>;
  type: StoreType;
  options?: { hydrateAtLoadTime?: boolean };
};
interface PersistentStoreInstance<State> extends BaseStoreInstance<State> {
  waitForInitialHydration: () => Promise<void>;
  purgePersistentLayer: () => Promise<void>;
  storageName: string;
}

export interface LocalStorageStoreOptions<State, PersistedState = State> extends PersistentStoreOptions<State, PersistedState> {}

export interface IndexedDbStoreOptions<State, PersistedState = State> extends PersistentStoreOptions<State, PersistedState> {}

export interface IndexedDBStoreInstance<State> extends PersistentStoreInstance<State> {
  type: StoreType.indexedDB;
  dbName: string;
}

export interface LocalStorageStoreInstance<State> extends PersistentStoreInstance<State> {
  type: StoreType.localStorage;
}

export interface NonPersistentStoreInstance<State> extends BaseStoreInstance<State> {
  type: StoreType.nonPersistent;
}

export type StoreInstance<State> = IndexedDBStoreInstance<State> | LocalStorageStoreInstance<State> | NonPersistentStoreInstance<State>;

export function isNonPersistentStoreInstance<State>(store: StoreInstance<State>): store is NonPersistentStoreInstance<State> {
  return store.type === StoreType.nonPersistent;
}

export function isIndexedDbStoreInstance<State>(store: StoreInstance<State>): store is IndexedDBStoreInstance<State> {
  return store.type === StoreType.indexedDB;
}

export function isLocalStorageStoreInstance<State>(store: StoreInstance<State>): store is LocalStorageStoreInstance<State> {
  return store.type === StoreType.localStorage;
}
